import React from 'react';
import { 
  Button, 
  Card, 
  CardBody, 
  CardHeader, 
  CardTitle,
  EmptyState,
  EmptyStateBody
} from '@patternfly/react-core';
import { 
  BellIcon, 
  CheckCircleIcon, 
  ExclamationCircleIcon, 
  ExclamationTriangleIcon,
  InfoCircleIcon
} from '@patternfly/react-icons';

// Mock data structure matching common enterprise event levels
interface NotificationItem {
  id: number;
  severity: 'success' | 'warning' | 'danger' | 'info';
  title: string;
  timestamp: string;
  description?: string;
}

const dummyNotifications: NotificationItem[] = [
  {
    id: 1,
    severity: 'danger',
    title: 'Deployment sync failure in route controller',
    timestamp: 'Just now',
    description: 'Service mesh unable to reconcile endpoint targets.'
  },
  {
    id: 2,
    severity: 'warning',
    title: 'High connection pool utilization detected',
    timestamp: '10m ago',
    description: 'Database pool capacity reached 85%.'
  },
  {
    id: 3,
    severity: 'success',
    title: 'Configuration sync successful',
    timestamp: '1h ago'
  },
  {
    id: 4,
    severity: 'info',
    title: 'Cluster routine backup completed',
    timestamp: '2h ago'
  }
];

// Helper to map severity to the correct PatternFly icon and token-like style mapping
const getSeverityIcon = (severity: string) => {
  switch (severity) {
    case 'danger':
      return <ExclamationCircleIcon style={{ color: 'var(--pf-t--global--icon--color--status--danger--default, #c9190b)' }} />;
    case 'warning':
      return <ExclamationTriangleIcon style={{ color: 'var(--pf-t--global--icon--color--status--warning--default, #f0ab00)' }} />;
    case 'success':
      return <CheckCircleIcon style={{ color: 'var(--pf-t--global--icon--color--status--success--default, #3f9c35)' }} />;
    case 'info':
    default:
      return <InfoCircleIcon style={{ color: 'var(--pf-t--global--icon--color--status--info--default, #0066cc)' }} />;
  }
};

export const DashboardNotificationsCard: React.FC = () => {
  return (
    <Card isFullHeight>
      <CardHeader
        actions={{
          actions: (
            <Button variant="link" isInline onClick={() => console.log('View all clicked')}>
              View all
            </Button>
          )
        }}
      >
        <CardTitle>
          Latest notifications &nbsp;
          <span style={{ fontSize: 'var(--pf-t--global--font--size--sm, 12px)', color: 'var(--pf-t--global--text--color--subtle, #6a6e73)' }}>(dummy data)</span>
        </CardTitle>
      </CardHeader>
      
      <CardBody>
        {dummyNotifications.length === 0 ? (
          <EmptyState variant="sm" titleText="No notifications" icon={BellIcon}>
            <EmptyStateBody>All systems are operating normally.</EmptyStateBody>
          </EmptyState>
        ) : (
          <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
            {dummyNotifications.map((notification, index) => (
              <li 
                key={notification.id} 
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  paddingBottom: '12px',
                  marginBottom: '12px',
                  borderBottom: index !== dummyNotifications.length - 1 ? '1px solid var(--pf-t--global--border--color--default, #d2d2d2)' : 'none'
                }}
              >
                {/* Status Icon */}
                <div style={{ marginRight: '12px', marginTop: '2px', display: 'flex' }}>
                  {getSeverityIcon(notification.severity)}
                </div>
                
                {/* Content Area */}
                <div style={{ flexGrow: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '14px', paddingRight: '8px' }}>
                      {notification.title}
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--pf-t--global--text--color--subtle, #6a6e73)', whiteSpace: 'nowrap' }}>
                      {notification.timestamp}
                    </span>
                  </div>
                  {notification.description && (
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--pf-t--global--text--color--regular, #151515)' }}>
                      {notification.description}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
};